import React, { useState, useEffect } from 'react';
import voiceService from '../../services/voiceService';
import { VoiceSettings as IVoiceSettings, TTSEngine } from '../../types';

// Функция для воспроизведения звука таймера (та же, что в SimpleChatInterface)
async function playTimerSound(): Promise<void> {
  try {
    const voiceSettings = voiceService.getSettings();
    
    if (voiceSettings.timerSoundEnabled === false) {
      return;
    }
    
    const frequency = voiceSettings.timerSoundFrequency || 800;
    const duration = (voiceSettings.timerSoundDuration || 200) / 1000;
    const count = voiceSettings.timerSoundCount || 3;
    const delay = (voiceSettings.timerSoundDelay || 200) / 1000;
    
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    
    for (let i = 0; i < count; i++) {
      await new Promise<void>((resolve) => {
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);
        
        oscillator.frequency.value = frequency;
        oscillator.type = 'sine';
        
        const startTime = audioContext.currentTime + i * (duration + delay);
        
        gainNode.gain.setValueAtTime(0, startTime);
        gainNode.gain.linearRampToValueAtTime(0.3, startTime + 0.01);
        gainNode.gain.linearRampToValueAtTime(0, startTime + duration);
        
        oscillator.start(startTime);
        oscillator.stop(startTime + duration);
        
        setTimeout(() => resolve(), (duration + delay) * 1000);
      });
    }
  } catch (error) {
    console.warn('Не удалось воспроизвести звук таймера:', error);
  }
}

interface VoiceSettingsProps {
  onClose?: () => void;
  isInline?: boolean; // true когда используется внутри другого компонента
}

const SectionCard: React.FC<{
  title: string;
  description?: React.ReactNode;
  children: React.ReactNode;
}> = ({ title, description, children }) => (
  <section className="rounded-xl md:rounded-2xl border border-gray-100 bg-gray-50/80 p-3 md:p-4 lg:p-5 shadow-sm backdrop-blur-sm">
    <h3 className="text-sm md:text-base font-semibold text-gray-800 flex items-center gap-2 mb-2">
      {title}
    </h3>
    {description && (
      <p className="text-xs text-gray-500 mb-3 md:mb-4 leading-relaxed">{description}</p>
    )}
    {children}
  </section>
);

const VoiceSettings: React.FC<VoiceSettingsProps> = ({ onClose, isInline = false }) => {
  const [settings, setSettings] = useState<IVoiceSettings>(voiceService.getSettings());
  const [voices, setVoices] = useState<SpeechSynthesisVoice[]>([]);

  useEffect(() => {
    // Загружаем доступные голоса для браузерного TTS
    const loadVoices = () => {
      const availableVoices = voiceService.getAvailableVoices();
      setVoices(availableVoices);
    };

    loadVoices();
    
    // Голоса могут загружаться асинхронно
    if (speechSynthesis.onvoiceschanged !== undefined) {
      speechSynthesis.onvoiceschanged = loadVoices;
    }
  }, []);

  const handleTTSEngineChange = (engine: TTSEngine) => {
    const newSettings = { ...settings, ttsEngine: engine };
    setSettings(newSettings);
    voiceService.updateSettings(newSettings);
  };

  const handleLanguageChange = (language: string) => {
    const newSettings = { ...settings, language };
    setSettings(newSettings);
    voiceService.updateSettings(newSettings);
  };

  const handleBrowserVoiceChange = (voiceName: string) => {
    const selectedVoice = voices.find(v => v.name === voiceName) || null;
    const newSettings = { ...settings, voice: selectedVoice };
    setSettings(newSettings);
    voiceService.updateSettings(newSettings);
  };

  const handleYandexVoiceChange = (yandexVoice: string) => {
    const newSettings = { ...settings, yandexVoice };
    setSettings(newSettings);
    voiceService.updateSettings(newSettings);
  };

  const handleYandexEmotionChange = (emotion: 'good' | 'neutral' | 'evil') => {
    const newSettings = { ...settings, yandexEmotion: emotion };
    setSettings(newSettings);
    voiceService.updateSettings(newSettings);
  };

  const handleYandexSpeedChange = (speed: number) => {
    const newSettings = { ...settings, yandexSpeed: speed };
    setSettings(newSettings);
    voiceService.updateSettings(newSettings);
  };

  const handleRateChange = (rate: number) => {
    const newSettings = { ...settings, rate };
    setSettings(newSettings);
    voiceService.updateSettings(newSettings);
  };

  const handlePitchChange = (pitch: number) => {
    const newSettings = { ...settings, pitch };
    setSettings(newSettings);
    voiceService.updateSettings(newSettings);
  };

  const handleVolumeChange = (volume: number) => {
    const newSettings = { ...settings, volume };
    setSettings(newSettings);
    voiceService.updateSettings(newSettings);
  };

  const handleTestSpeech = async () => {
    const testText = 'Привет! Это тестовое сообщение для проверки озвучивания.';
    
    try {
      await voiceService.speak(testText);
    } catch (error) {
      console.error('Ошибка озвучивания:', error);
      alert('Ошибка озвучивания. Проверьте настройки.');
    }
  };

  const handleReset = () => {
    voiceService.resetSettings();
    setSettings(voiceService.getSettings());
  };

  const yandexVoices = [
    { value: 'alena', label: 'Alena (Женский, дружелюбный)' },
    { value: 'filipp', label: 'Filipp (Мужской, нейтральный)' },
    { value: 'ermil', label: 'Ermil (Мужской, нейтральный)' },
    { value: 'jane', label: 'Jane (Женский, нейтральный)' },
    { value: 'madirus', label: 'Madirus (Мужской, сказочный)' },
    { value: 'omazh', label: 'Omazh (Женский, нейтральный)' },
  ];

  const containerClasses = [
    'voice-settings w-full',
    isInline ? 'bg-transparent p-0' : 'p-4 md:p-6 bg-white/95 rounded-3xl shadow-2xl border border-gray-100'
  ].join(' ');

  return (
    <div className={containerClasses}>
      {!isInline && (
        <div className="flex flex-wrap gap-3 justify-between items-start mb-4 md:mb-6">
          <h2 className="text-xl md:text-2xl font-bold text-gray-800">⚙️ Настройки озвучивания</h2>
          {onClose && (
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 text-2xl md:text-3xl leading-none transition-transform hover:scale-110"
            >
              ×
            </button>
          )}
        </div>
      )}
      
      <div className="space-y-4 md:space-y-5">
        <SectionCard
          title="🎙️ Движок озвучивания"
          description={
            settings.ttsEngine === 'browser'
              ? '⚡ Быстрый, бесплатный, работает офлайн. Если браузерный TTS не работает, попробуйте Yandex TTS.'
              : '🎯 Высокое качество, естественная речь, надежное воспроизведение (требуется API ключ).'
          }
        >
          <div className="grid gap-2 md:gap-3 grid-cols-1 sm:grid-cols-2">
            <button
              onClick={() => handleTTSEngineChange('browser')}
              className={`rounded-xl md:rounded-2xl border px-3 md:px-4 py-2.5 md:py-3 text-sm md:text-base font-medium transition-all shadow-sm ${
                settings.ttsEngine === 'browser'
                  ? 'bg-blue-600 text-white border-blue-600 shadow-blue-200'
                  : 'bg-white text-gray-700 border-gray-200 hover:border-blue-300'
              }`}
            >
              🌐 Браузерный TTS
            </button>
            <button
              onClick={() => handleTTSEngineChange('yandex')}
              className={`rounded-xl md:rounded-2xl border px-3 md:px-4 py-2.5 md:py-3 text-sm md:text-base font-medium transition-all shadow-sm ${
                settings.ttsEngine === 'yandex'
                  ? 'bg-blue-600 text-white border-blue-600 shadow-blue-200'
                  : 'bg-white text-gray-700 border-gray-200 hover:border-blue-300'
              }`}
            >
              🇷🇺 Yandex TTS
            </button>
          </div>
        </SectionCard>

        <SectionCard title="🌍 Язык">
          <select
            value={settings.language}
            onChange={(e) => handleLanguageChange(e.target.value)}
            className="w-full rounded-xl md:rounded-2xl border border-gray-200 bg-white px-3 md:px-4 py-2.5 md:py-3 text-sm md:text-base focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="ru-RU">Русский (RU)</option>
          </select>
        </SectionCard>

      {/* Настройки для Yandex TTS */}
      {settings.ttsEngine === 'yandex' && (
        <>
          <SectionCard title="🎭 Голос Yandex">
            <select
              value={settings.yandexVoice || 'alena'}
              onChange={(e) => handleYandexVoiceChange(e.target.value)}
              className="w-full rounded-xl md:rounded-2xl border border-gray-200 bg-white px-3 md:px-4 py-2.5 md:py-3 text-sm md:text-base focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {yandexVoices.map(voice => (
                <option key={voice.value} value={voice.value}>
                  {voice.label}
                </option>
              ))}
            </select>
          </SectionCard>

          <SectionCard title="😊 Эмоция">
            <div className="grid gap-2 md:gap-3 grid-cols-3">
              {(['good', 'neutral', 'evil'] as const).map(emotion => (
                <button
                  key={emotion}
                  onClick={() => handleYandexEmotionChange(emotion)}
                  className={`rounded-xl md:rounded-2xl border px-2 md:px-3 py-2 md:py-2.5 text-xs md:text-sm font-medium transition-all ${
                    settings.yandexEmotion === emotion
                      ? 'bg-blue-600 text-white border-blue-600 shadow-blue-200'
                      : 'bg-white text-gray-700 border-gray-200 hover:border-blue-300'
                  }`}
                >
                  {emotion === 'good' && '😊 Добрая'}
                  {emotion === 'neutral' && '😐 Нейтральная'}
                  {emotion === 'evil' && '😈 Злая'}
                </button>
              ))}
            </div>
          </SectionCard>

          <SectionCard title={`⚡ Скорость Yandex: ${settings.yandexSpeed?.toFixed(1)}x`}>
            <input
              type="range"
              min="0.1"
              max="3.0"
              step="0.1"
              value={settings.yandexSpeed || 1.0}
              onChange={(e) => handleYandexSpeedChange(parseFloat(e.target.value))}
              className="w-full accent-blue-600 h-2 md:h-3"
            />
            <div className="mt-1.5 md:mt-2 flex justify-between text-xs text-gray-500">
              <span className="text-[10px] md:text-xs">0.1x (медленно)</span>
              <span className="text-[10px] md:text-xs">1.0x (нормально)</span>
              <span className="text-[10px] md:text-xs">3.0x (быстро)</span>
            </div>
          </SectionCard>
        </>
      )}

      {/* Настройки для браузерного TTS */}
      {settings.ttsEngine === 'browser' && (
        <>
          {voices.length > 0 && (
            <SectionCard title="🎭 Голос браузера">
              <select
                value={settings.voice?.name || ''}
                onChange={(e) => handleBrowserVoiceChange(e.target.value)}
                className="w-full rounded-xl md:rounded-2xl border border-gray-200 bg-white px-3 md:px-4 py-2.5 md:py-3 text-sm md:text-base focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">По умолчанию</option>
                {voices.map(voice => (
                  <option key={voice.name} value={voice.name}>
                    {voice.name} ({voice.lang})
                  </option>
                ))}
              </select>
            </SectionCard>
          )}

          <SectionCard title={`⚡ Скорость: ${settings.rate.toFixed(1)}x`}>
            <input
              type="range"
              min="0.1"
              max="2.0"
              step="0.1"
              value={settings.rate}
              onChange={(e) => handleRateChange(parseFloat(e.target.value))}
              className="w-full accent-blue-600 h-2 md:h-3"
            />
          </SectionCard>

          <SectionCard title={`🎵 Тон: ${settings.pitch.toFixed(1)}`}>
            <input
              type="range"
              min="0"
              max="2"
              step="0.1"
              value={settings.pitch}
              onChange={(e) => handlePitchChange(parseFloat(e.target.value))}
              className="w-full accent-blue-600 h-2 md:h-3"
            />
          </SectionCard>
        </>
      )}

      <SectionCard title={`🔊 Громкость: ${Math.round(settings.volume * 100)}%`}>
        <input
          type="range"
          min="0"
          max="1"
          step="0.1"
          value={settings.volume}
          onChange={(e) => handleVolumeChange(parseFloat(e.target.value))}
          className="w-full accent-blue-600 h-2 md:h-3"
        />
      </SectionCard>

      <SectionCard title="🔊 Автоматически озвучивать ответы LLM">
        <label className="flex items-start gap-3 md:gap-4 cursor-pointer">
          <input
            type="checkbox"
            checked={settings.autoSpeak}
            onChange={(e) => {
              const newSettings = { ...settings, autoSpeak: e.target.checked };
              setSettings(newSettings);
              voiceService.updateSettings(newSettings);
            }}
            className="h-4 w-4 md:h-5 md:w-5 mt-0.5 rounded border border-gray-300 text-blue-600 focus:ring-2 focus:ring-blue-500 flex-shrink-0"
          />
          <p className="text-xs md:text-sm text-gray-600 leading-relaxed">
            Если включено, ответы ассистента автоматически озвучиваются выбранным голосом.
          </p>
        </label>
      </SectionCard>

      <SectionCard title="🎤 Автоматически отправлять сообщение после распознавания речи">
        <label className="flex items-start gap-3 md:gap-4 cursor-pointer">
          <input
            type="checkbox"
            checked={settings.autoSendAfterRecognition !== false}
            onChange={(e) => {
              const newSettings = { ...settings, autoSendAfterRecognition: e.target.checked };
              setSettings(newSettings);
              voiceService.updateSettings(newSettings);
            }}
            className="h-4 w-4 md:h-5 md:w-5 mt-0.5 rounded border border-gray-300 text-blue-600 focus:ring-2 focus:ring-blue-500 flex-shrink-0"
          />
          <p className="text-xs md:text-sm text-gray-600 leading-relaxed">
            Если включено, сообщение автоматически отправляется после распознавания речи. 
            Если отключено, распознанный текст будет помещен в поле ввода для редактирования.
          </p>
        </label>
      </SectionCard>

      <div className="grid gap-3 md:gap-4 grid-cols-1 sm:grid-cols-2">
        <button
          onClick={handleTestSpeech}
          className="rounded-xl md:rounded-2xl bg-green-600 py-2.5 md:py-3 px-4 md:px-6 text-sm md:text-base font-semibold text-white shadow-lg shadow-green-200 transition-transform hover:-translate-y-0.5 hover:bg-green-700"
        >
          🔊 Тест
        </button>
        <button
          onClick={handleReset}
          className="rounded-xl md:rounded-2xl bg-gray-600 py-2.5 md:py-3 px-4 md:px-6 text-sm md:text-base font-semibold text-white shadow-lg shadow-gray-200 transition-transform hover:-translate-y-0.5 hover:bg-gray-700"
        >
          🔄 Сброс
        </button>
      </div>

      <SectionCard
        title="💡 Совет"
        description={
          <>
            Yandex TTS обеспечивает более естественное звучание, но требует настроенный API ключ
            в файле <code className="rounded bg-white px-1">.env</code>. Браузерный TTS работает без дополнительной настройки.
          </>
        }
      >
        <div className="flex items-center justify-between text-sm text-gray-600">
          <span>Не забудьте протестировать голос после изменения параметров.</span>
        </div>
      </SectionCard>
      </div>

      {/* Отдельная секция для настроек звука таймера - визуально выделена */}
      <div className="mt-4 md:mt-6 pt-4 md:pt-6 border-t-2 border-orange-200">
        <section className="rounded-xl md:rounded-2xl border-2 border-orange-300 bg-orange-50/80 p-3 md:p-4 lg:p-5 shadow-md backdrop-blur-sm">
          <h3 className="text-sm md:text-base font-semibold text-orange-800 flex items-center gap-2 mb-2">
            ⏰ Звук таймера
          </h3>
          <p className="text-xs text-orange-600 mb-3 md:mb-4 leading-relaxed">
            Настройки звукового сигнала, который воспроизводится при завершении таймера.
          </p>
          
          <div className="space-y-4">
            <label className="flex items-start gap-3 md:gap-4 cursor-pointer">
              <input
                type="checkbox"
                checked={settings.timerSoundEnabled !== false}
                onChange={(e) => {
                  const newSettings = { ...settings, timerSoundEnabled: e.target.checked };
                  setSettings(newSettings);
                  voiceService.updateSettings(newSettings);
                }}
                className="h-4 w-4 md:h-5 md:w-5 mt-0.5 rounded border border-orange-300 text-orange-600 focus:ring-2 focus:ring-orange-500 flex-shrink-0"
              />
              <p className="text-xs md:text-sm text-orange-700 leading-relaxed">
                Воспроизводить звуковой сигнал при завершении таймера.
              </p>
            </label>

            {settings.timerSoundEnabled !== false && (
              <>
                <div>
                  <label className="block text-xs md:text-sm font-medium text-orange-800 mb-2">
                    Частота: {settings.timerSoundFrequency || 800} Гц
                  </label>
                  <input
                    type="range"
                    min="200"
                    max="2000"
                    step="50"
                    value={settings.timerSoundFrequency || 800}
                    onChange={(e) => {
                      const newSettings = { ...settings, timerSoundFrequency: parseInt(e.target.value) };
                      setSettings(newSettings);
                      voiceService.updateSettings(newSettings);
                    }}
                    className="w-full accent-orange-600 h-2 md:h-3"
                  />
                  <div className="mt-1.5 md:mt-2 flex justify-between text-[10px] md:text-xs text-orange-600">
                    <span>200 Гц (низкий)</span>
                    <span>800 Гц (средний)</span>
                    <span>2000 Гц (высокий)</span>
                  </div>
                </div>

                <div>
                  <label className="block text-xs md:text-sm font-medium text-orange-800 mb-2">
                    Длительность сигнала: {settings.timerSoundDuration || 200} мс
                  </label>
                  <input
                    type="range"
                    min="50"
                    max="500"
                    step="50"
                    value={settings.timerSoundDuration || 200}
                    onChange={(e) => {
                      const newSettings = { ...settings, timerSoundDuration: parseInt(e.target.value) };
                      setSettings(newSettings);
                      voiceService.updateSettings(newSettings);
                    }}
                    className="w-full accent-orange-600 h-2 md:h-3"
                  />
                </div>

                <div>
                  <label className="block text-xs md:text-sm font-medium text-orange-800 mb-2">
                    Количество сигналов: {settings.timerSoundCount || 3}
                  </label>
                  <input
                    type="range"
                    min="1"
                    max="5"
                    step="1"
                    value={settings.timerSoundCount || 3}
                    onChange={(e) => {
                      const newSettings = { ...settings, timerSoundCount: parseInt(e.target.value) };
                      setSettings(newSettings);
                      voiceService.updateSettings(newSettings);
                    }}
                    className="w-full accent-orange-600 h-2 md:h-3"
                  />
                </div>

                <div>
                  <label className="block text-xs md:text-sm font-medium text-orange-800 mb-2">
                    Задержка между сигналами: {settings.timerSoundDelay || 200} мс
                  </label>
                  <input
                    type="range"
                    min="50"
                    max="500"
                    step="50"
                    value={settings.timerSoundDelay || 200}
                    onChange={(e) => {
                      const newSettings = { ...settings, timerSoundDelay: parseInt(e.target.value) };
                      setSettings(newSettings);
                      voiceService.updateSettings(newSettings);
                    }}
                    className="w-full accent-orange-600 h-2 md:h-3"
                  />
                </div>

                <button
                  onClick={async () => {
                    try {
                      await playTimerSound();
                    } catch (error) {
                      console.error('Ошибка тестирования звука таймера:', error);
                    }
                  }}
                  className="w-full rounded-xl md:rounded-2xl bg-orange-600 py-2 md:py-2.5 px-4 text-sm md:text-base font-semibold text-white shadow-lg shadow-orange-200 transition-transform hover:-translate-y-0.5 hover:bg-orange-700"
                >
                  🔊 Тест звука таймера
                </button>
              </>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

export default VoiceSettings;

